import moment from "moment-timezone";
import { isEmpty, map, find, includes, filter, startCase, flatten, uniqBy, sortBy as lodashSortBy, concat, uniq, forEach } from "lodash";

import { ApiActions } from "middleware/call-api";
import { postDownloadFromApi, postFetchFromApi } from "utils/download-from-api";
import { getReferralWorkflowStateSettings, getAllTriageCategorySettings, getReferralWorklistSettings, getReferralSortByOptions, getAllRoles, getMaxItemsToDisplayForReferralWorklistMultiselectFilters } from "app/auth/store/reducers/system-configuration";
import { getUser, getSignedInOrgUnit } from "app/auth/store/reducers/user.reducer";
import getDefaultWorkListFilterOption from "helpers/get-default-worklist-filter-option";
import { ActionTypes as ReferralsActionTypes } from "app/main/referrals/reducers/referrals.reducers";
import { getCurrentOrgUnitFormFields } from "app/main/orgUnits/reducers/orgUnit.reducers";

import prepareSearchReferralsParamsForBody from "helpers/format-search-referrals-params-for-request-body";
import { ActionTypes, bailOutOfLoadingReferrals, shouldShowStatusSearchTags, getDefaultFilterOptionSearchParams, getDefaultSortOrder, getSearchParameters, IsEnableClinicalRoleReferralActiveView, IsEnableReferralWorklistDefaultMyReferrals } from "../reducers/referralWorklist.reducers";

const getDefaultSpecialtySearchParams = (state, presetFilter) => {
  const currentUser = getUser(state);
  const signedInOrgUnit = getSignedInOrgUnit(state);
  const orgUnitId = signedInOrgUnit?.id;

  const { userSpecialtyProfiles, userRoles, userId } = currentUser;
  const assignedSpecialtyProfiles = filter(userSpecialtyProfiles, userSpecialtyProfile => userSpecialtyProfile.specialtyProfile?.orgUnitId === orgUnitId);
  const currentUserRoleIds = map(filter(userRoles, x => x.orgUnitId === orgUnitId), r => r.roleId);
  const specialtyProfileAvailableRoles = flatten(map(assignedSpecialtyProfiles, x => x.specialtyProfileAvailableRoleIds));

  const allRoles = getAllRoles(state);
  const referralWorklistSettings = getReferralWorklistSettings(state);

  // TODO: determine the desired functionality for this setting for filters which set the assignedToRoleIds field themselves (i.e. clinicSpecialistFilter and unAssignedReferrals).
  const assignedToRoleIds = referralWorklistSettings?.enableWorkListSearchFilterAutoAssignedToRole
    ? map(filter(specialtyProfileAvailableRoles, x => includes(currentUserRoleIds, x)), r => r)
    : null;

  let defaultParams = {
    assignedToSpecialtyIds: map(assignedSpecialtyProfiles, x => x.specialtyProfile?.id),
    assignedToSubSpecialtyIds: map(assignedSpecialtyProfiles, x => x.subSpecialty?.id),
    assignedToRoleIds,
  };

  if (presetFilter === "myOutstandingTasks") {
    defaultParams = {
      ...defaultParams,
      taskStatus: "OutstandingTasks",
      taskAssignedToUserId: userId,
      taskAssignedToRoleIds: map(userRoles, role => role.roleId),
    };
  }

  if (presetFilter === "myReferrals") {
    // My Referral filter should just restrict based on the created by user field
    defaultParams = {
      createdByUserId: userId,
      includeAllSites: true,
    };
  }

  // TODO: Move this logic to a single location -- see also getPresetParams
  const clinicalSpecialistRole = find(allRoles, (x => x.roleName === "ClinicSpecialist"));
  if (presetFilter === "clinicSpecialistFilter") {
    defaultParams = {
      ...defaultParams,
      assignedToRoleIds: clinicalSpecialistRole ? [clinicalSpecialistRole.roleId] : null,
      status: ["Requested", "AwaitingInformation"],
    };
  }

  if (presetFilter === "unAssignedReferrals") {
    defaultParams = {
      ...defaultParams,
      onlyShowUnassigned: true,
      status: ["Draft", "Requested"],
      assignedToSpecialtyIds: null,
      assignedToRoleIds: null,
      assignedToRoles: null,
      assignedToUserId: null,
    };
  }

  return defaultParams;
};

export const updateReferralWorkListFilter = (presetFilter, showStatusTags) => (dispatch, getState) => new Promise(resolve => {
  const defaultSpecialtySearchParams = getDefaultSpecialtySearchParams(getState(), presetFilter);
  const defaultSortOrderSearchParam = getDefaultSortOrder(getState());
  const lastSearchParams = getSearchParameters(getState());
  const allRoles = getAllRoles(getState());
  // try to get the user selected sort order from lastSearchParams first and then get defaultSortOrder if the sorOrder in lastSearchParams is empty.
  const sortOrderDescending = lastSearchParams?.sortOrderDescending ?? defaultSortOrderSearchParam?.sortOrderDescending;
  const preSetSearchParams = { ...defaultSpecialtySearchParams, ...defaultSortOrderSearchParam, sortOrderDescending };

  dispatch({ type: ActionTypes.UPDATE_REFERRAL_WORKLIST_FILTER, payload: { presetFilter, showStatusTags, preSetSearchParams, allRoles } });
  const state = getState();
  resolve(state?.referralWorklist.searchParams);
});

export const setUpReferralWorklistFilter = () => (dispatch, getState) => {
  const state = getState();
  const settings = getReferralWorkflowStateSettings(state);
  const defaultSortOrderSearchParam = getDefaultSortOrder(state);
  const referralWorklistSettings = getReferralWorklistSettings(state);
  const defaultWorkListSearchFilterRoleTypeSettings = referralWorklistSettings?.defaultWorkListSearchFilterRoleTypeSettings;
  const user = getUser(state);
  const signedInOrgUnit = getSignedInOrgUnit(state);
  const currentUserRole = find(user.userRoles, x => x.orgUnitId === signedInOrgUnit.id);
  const isEnableClinicalRoleReferralActiveView = IsEnableClinicalRoleReferralActiveView(state);
  const isEnableReferralWorklistDefaultMyReferrals = IsEnableReferralWorklistDefaultMyReferrals(state);
  const defaultSearchType = getDefaultWorkListFilterOption(defaultWorkListSearchFilterRoleTypeSettings, currentUserRole, isEnableClinicalRoleReferralActiveView, isEnableReferralWorklistDefaultMyReferrals);
  const defaultSpecialtySearchParams = getDefaultSpecialtySearchParams(state, defaultSearchType);
  const allRoles = getAllRoles(getState());

  // set to default user's specialty and role (if has been set)
  return dispatch({
    type: ActionTypes.SETUP_REFERRAL_WORKLIST_FILTERS,
    payload: {
      settings,
      defaultSearchType,
      allRoles,
      searchParams: { ...defaultSpecialtySearchParams, ...defaultSortOrderSearchParam },
      referralWorklistSettings,
      isEnableClinicalRoleReferralActiveView,
      isEnableReferralWorklistDefaultMyReferrals,
    },
  });
};

export const resetReferralWorklistFilter = defaultType => (dispatch, getState) => {
  const defaultSearchParams = getDefaultFilterOptionSearchParams(getState());
  const defaultSpecialtySearchParams = getDefaultSpecialtySearchParams(getState(), defaultType);
  const defaultSortOrderSearchParam = getDefaultSortOrder(getState());

  return dispatch({
    type: ActionTypes.RESET_REFERRAL_WORKLIST_FILTER,
    payload: {
      searchParams: {
        ...defaultSearchParams,
        ...defaultSpecialtySearchParams,
        ...defaultSortOrderSearchParam,
      },
    },
  });
};

export const searchReferralResults = (searchParams, pageNumber = 1, pageSize = 20, forceLoad = false) => (dispatch, getState) => {
  const timeZone = moment.tz.guess();
  const defaultSortOrder = getDefaultSortOrder(getState);

  const formattedSearchParams = {
    ...searchParams,
    sortOrderDescending: searchParams?.sortOrderDescending ?? defaultSortOrder?.sortOrderDescending,
    applyUrgentReferralFirst: searchParams?.applyUrgentReferralFirst ?? defaultSortOrder?.applyUrgentReferralFirst,
  };

  const defaultSearchParams = {
    ...getDefaultFilterOptionSearchParams(getState()),
    assignedToSpecialtyIds: searchParams.assignedToSpecialtyIds,
    assignedToRoleIds: searchParams.assignedToRoleIds,
    ...defaultSortOrder,
    assignedToSpecialtyId: searchParams.assignedToSpecialtyId,
  };
  const shouldLoadLastReferralSelectSearchParams = !isEmpty(localStorage.getItem("lastSearchParams")) && JSON.stringify(defaultSearchParams) === JSON.stringify(searchParams);

  if (JSON.stringify(defaultSearchParams) !== JSON.stringify(formattedSearchParams) || localStorage.getItem("lastSelectReferralSearchOption") === "active" || localStorage.getItem("lastSelectReferralSearchOption") === "clinicalRoleActiveView") {
    localStorage.setItem("lastSearchParams", JSON.stringify(formattedSearchParams));
  }
  localStorage.setItem("shouldLoadLastReferralSelectSearchParams", shouldLoadLastReferralSelectSearchParams);
  let params = searchParams;
  const referralWorklistSettings = getReferralWorklistSettings(getState());
  if (shouldLoadLastReferralSelectSearchParams) {
    params = JSON.parse(localStorage.getItem("lastSearchParams"));
    if (!referralWorklistSettings?.setLastSelectReferralSortOrder) {
      params = {
        ...params,
        sortOrderDescending: getDefaultSortOrder(getState)?.sortOrderDescending,
      };
    }
  }

  const processedParams = prepareSearchReferralsParamsForBody(params);
  const url = `/referrals/${searchParams.orgUnitId}/searchReferrals?pageNumber=${pageNumber}&pageSize=${pageSize}&timeZone=${timeZone}`;

  return dispatch({
    [ApiActions.FETCH_FROM_API]: {
      endpoint: url,
      method: "POST",
      body: { searchParams: processedParams },
      bailout: !forceLoad && bailOutOfLoadingReferrals(getState(), pageNumber),
      types: [
        ActionTypes.SEARCHING_REFERRALS,
        ActionTypes.SEARCHED_REFERRALS,
        ActionTypes.ERROR_SEARCHING_REFERRALS,
      ],
      pageNumber,
    },
  });
};

export const exportReferralResults = (
  searchParams,
  backgroundExport = false,
  expectedTotalRecords,
  pageNumber = 1,
  pageSize = 20,
) => async () => {
  const processedParams = prepareSearchReferralsParamsForBody(searchParams);
  const postBody = {
    searchParams: processedParams,
  };
  const timeZone = moment.tz.guess();
  const url = `api/referrals/${processedParams.orgUnitId}/export?pageNumber=${pageNumber}&pageSize=${pageSize}&timeZone=${timeZone}&expectedTotalRecords=${expectedTotalRecords}`;

  if (backgroundExport) {
    return postFetchFromApi(url, postBody);
  }

  return postDownloadFromApi(url, postBody);
};

export const clearReferralWorkListFilterTags = () => ({
  type: ActionTypes.RESET_REFERRAL_WORKLIST_FILTER_TAGS,
});

export const formattedReferralWorkListFilterTags = (searchParams, referralFieldLabels, getState) => {
  if (!searchParams) return null;
  const state = getState();
  const referralWorkflowSettings = getReferralWorkflowStateSettings(state);
  const triageCategorySettings = getAllTriageCategorySettings(state);
  const showStatusTags = shouldShowStatusSearchTags(state);
  const sortByOptions = getReferralSortByOptions(state);
  const maxMultiselectItems = getMaxItemsToDisplayForReferralWorklistMultiselectFilters(state);

  const getWorkflowStateLabel = stateName => find(referralWorkflowSettings, x => x.workflowState === stateName)?.label || stateName;
  const getTriageCategoryLabel = category => find(triageCategorySettings, choice => choice.triageCategory === category)?.label;

  const generateMultiselectSummaryLabel = items => {
    let label = "";

    if (!items) return label;

    const totalItems = items.length;
    const itemsToShow = items.slice(0, maxMultiselectItems);
    const remainingItemsCount = totalItems - maxMultiselectItems;

    itemsToShow.forEach(value => {
      label = label ? `${label}, ` : "";
      label = `${label}${value}`;
    });

    if (remainingItemsCount > 0) {
      label = `${label}, (+${remainingItemsCount} more)`;
    }

    return label;
  };

  const {
    orgUnitId,
    status,
    category,
    startDate,
    endDate,
    unreadAddendum,
    isUrgent,
    isWardRequest,
    onlyShowUnassigned,
    assignedTo,
    assignedToSpecialtyIds,
    assignedToRoleIds,
    assignedToRoles,
    assignedToUserId,
    daysSinceLastStatusChange,
    assignedToSubSpecialties,
    taskStatus,
    taskAssignedToUserId,
    taskAssignedToRoleIds,
    taskAssignedToUser,
    taskAssignedToRoles,
    createdByUserId,
    createdByUser,
    assignedToSpecialistId,
    sortBy,
    sortOrderDescending,
    applyUrgentReferralFirst,
    isReferrerUrgent,
    presentingComplaintCodes,
    presentingComplaintCodesDisplay,
    referrerAdvisedPriorityCodeIds,
    referrerAdvisedPriorityCodes,
    referringOrgUnitNames,
    referringLocationCodeDisplayNames,
    reasonForReferralCodeDisplayNames,
    typeOfReferralCodeDisplayNames,
    onlyUnassignedReferrerAdvisedPriority,
    includeAllSites,
  } = searchParams;

  let categoryTags = null;
  let statusTags = null;
  let assignedToSpecialtyLabel = null;
  let assignedToUser = null;
  let assignedToSpecialistLabel = null;
  let assignedToSpecialtyRoleLabel = null;
  const specialtyProfileFromSubSpecialty = map(uniqBy(assignedToSubSpecialties, "specialtyProfileName"), x => x.specialtyProfileName);
  const allAssignedSpecialtyProfileNames = lodashSortBy(uniq(concat(map(assignedTo, x => x.specialtyProfileName), specialtyProfileFromSubSpecialty)));
  const presentingComplaintCodesTags = presentingComplaintCodes ? generateMultiselectSummaryLabel(map(presentingComplaintCodesDisplay, x => x.displayName)) : null;
  let referrerAdvisedPriorityCodesTags = null;
  let onlyUnassignedReferrerAdvisedPriorityLabel = null;
  let includeAllSitesTag = null;

  if (assignedTo) {
    if (assignedToSpecialtyIds) {
      forEach(allAssignedSpecialtyProfileNames, value => {
        assignedToSpecialtyLabel = isEmpty(assignedToSpecialtyLabel) ? "" : `${assignedToSpecialtyLabel}, `;
        if (includes(specialtyProfileFromSubSpecialty, value)) {
          const subSpecialtiesBySpecialty = filter(assignedToSubSpecialties, assignedToSubSpecialty => assignedToSubSpecialty.specialtyProfileName === value);
          assignedToSpecialtyLabel = `${assignedToSpecialtyLabel}${value} (${map(subSpecialtiesBySpecialty, x => x.displayName).join(", ")})`;
        } else {
          assignedToSpecialtyLabel = `${assignedToSpecialtyLabel}${value}`;
        }
      });
    }

    if (assignedToUserId) {
      assignedToUser = assignedTo.userName;
    }

    if (assignedToSpecialistId) {
      assignedToSpecialistLabel = map(assignedTo, x => ` ${x.specialistName}`);
    }
  }

  if (assignedToUserId) {
    assignedToUser = searchParams.assignedToUser;
  }

  if (assignedToRoleIds) {
    assignedToSpecialtyRoleLabel = generateMultiselectSummaryLabel(map(assignedToRoles, x => x.roleDescription ?? startCase(x.roleName)));
  }

  if (!isEmpty(status)) {
    statusTags = `${map(status, s => ` ${getWorkflowStateLabel(s)}`)}`;
  }
  if (!isEmpty(category)) {
    categoryTags = `${map(category, c => ` ${getTriageCategoryLabel(c)}`)}`;
  }
  const sortByDateLabel = sortBy ? find(sortByOptions, x => x.value === sortBy)?.label : null;
  const sortByUrgentReferralFirstLabel = applyUrgentReferralFirst === true ? "Urgent on Top, " : "";

  if (referrerAdvisedPriorityCodeIds) {
    referrerAdvisedPriorityCodesTags = generateMultiselectSummaryLabel(map(referrerAdvisedPriorityCodes, x => x.displayName));
  }

  if (onlyUnassignedReferrerAdvisedPriority) {
    const triageFieldSettings = getCurrentOrgUnitFormFields(state, orgUnitId, "TriageForm");
    const rejectFieldSettings = getCurrentOrgUnitFormFields(state, orgUnitId, "RejectForm");
    const referrerAdvisedPriorityLabel = (triageFieldSettings?.referrerAdvisedPriority?.label ?? rejectFieldSettings?.referrerAdvisedPriority?.label) || "Referrer Advised Priority";
    onlyUnassignedReferrerAdvisedPriorityLabel = `Not Assigned To ${referrerAdvisedPriorityLabel}`;
  }

  if (includeAllSites) {
    includeAllSitesTag = "All Sites";
  }

  const tags = [
    showStatusTags && statusTags,
    showStatusTags && daysSinceLastStatusChange && `${daysSinceLastStatusChange} Day${daysSinceLastStatusChange > 1 ? "s" : ""} Waiting in Status`,
    categoryTags && generateMultiselectSummaryLabel(categoryTags.split(",")),
    startDate && endDate && `Referrals Dated from ${startDate} to ${endDate}`,
    unreadAddendum && "Has Unread Updates",
    (isUrgent || isReferrerUrgent) && "Marked as Urgent",
    isWardRequest && (referralFieldLabels?.wardRequest ? referralFieldLabels?.wardRequest : "Ward Request"),
    assignedToSpecialtyLabel && `Assigned to ${generateMultiselectSummaryLabel(assignedToSpecialtyLabel.split(/,(?![^(]*\))/))}`, // split by comma but only when not within parentheses to avoid splitting on sub-specialties
    assignedToSpecialtyRoleLabel && `Assigned to Role ${assignedToSpecialtyRoleLabel}`,
    assignedToSpecialistLabel && `Assigned to Specialist ${assignedToSpecialistLabel}`,
    assignedToUserId && `Assigned to ${assignedToUser}`,
    onlyShowUnassigned && "Not Assigned to a Specialty",
    taskStatus === "CompletedTasks" && "Has Completed Tasks",
    taskStatus === "OutstandingTasks" && "Has Outstanding Tasks",
    taskStatus === "OverdueTasks" && "Has Overdue Tasks",
    taskAssignedToUserId && `Tasks Assigned To ${taskAssignedToUser}`,
    taskAssignedToRoleIds && `Tasks Assigned To ${generateMultiselectSummaryLabel(taskAssignedToRoles)}`,
    createdByUserId && `Referrals Created by ${createdByUser}`,
    includeAllSitesTag,
    presentingComplaintCodesTags,
    referrerAdvisedPriorityCodesTags,
    onlyUnassignedReferrerAdvisedPriorityLabel,
    sortByDateLabel && `${sortByUrgentReferralFirstLabel}${sortByDateLabel} ${sortOrderDescending ? "\u2191" : "\u2193"}`,
    referringOrgUnitNames && `Referring to ${referringOrgUnitNames && generateMultiselectSummaryLabel(referringOrgUnitNames.split(","))}`,
    referringLocationCodeDisplayNames && generateMultiselectSummaryLabel(referringLocationCodeDisplayNames.split(",")),
    reasonForReferralCodeDisplayNames && generateMultiselectSummaryLabel(reasonForReferralCodeDisplayNames.split(",")),
    typeOfReferralCodeDisplayNames && `Show ${generateMultiselectSummaryLabel(typeOfReferralCodeDisplayNames.split(","))} referrals`,
  ];
  return tags;
};

export const updateReferralWorkListFilterTags = (searchParams, referralFieldLabels) => (dispatch, getState) => {
  const tags = formattedReferralWorkListFilterTags(searchParams, referralFieldLabels, getState);

  return dispatch({
    type: ActionTypes.UPDATE_REFERRAL_WORKLIST_FILTER_TAGS,
    payload: tags,
  });
};

export const updateReferralsAssignedSite = () => dispatch => dispatch({
  type: ReferralsActionTypes.MOVE_CURRENT_REFERRAL,
});

export const clearLastSearchParams = () => dispatch => dispatch({
  type: ActionTypes.CLEAR_LAST_SEARCH_PARAMS,
});
